/**
 * hubspot-form-a11y.js
 * -----------------------------------------------------------------------------
 * Accessibility remediation for the embedded HubSpot form.
 *
 * FIXES  WCAG 2.1 Level A - H71 / 1.3.1 / 3.3.2
 *        "All fieldset elements should be labeled with legend elements"
 *
 * APPROACH
 * HubSpot's legacy renderer wraps every form ROW in a <fieldset>. It uses them
 * for layout, not semantics - a <fieldset> around a single text input is not a
 * group, and grouping one control is meaningless. Rather than invent a <legend>
 * for a group that does not exist, this removes the fieldsets that were never
 * real groups. HubSpot reached the same conclusion: their updated form renderer
 * emits no <fieldset> elements at all.
 *
 * WHAT IT DOES
 *   1. Converts orphan <legend> elements (HubSpot nests field help text in a
 *      <legend> inside a <div>, which is invalid HTML) into <div> with the same
 *      class, so appearance is unchanged.
 *   2. Unwraps layout fieldsets into <div>, carrying every attribute over so
 *      CSS hooks such as .form-columns-1 / .form-columns-2 keep matching.
 *   3. Leaves GENUINE groups alone - a fieldset holding two or more radios or
 *      checkboxes that share a name - and ensures those have a first-child
 *      <legend>. The current form has none; this is future-proofing in case a
 *      radio group is added in HubSpot later.
 *
 * REQUIRES
 * hubspot-contact-form.css must not qualify selectors with the `fieldset` tag.
 * Those were changed to class-only selectors (.form-columns-1 .input rather
 * than fieldset.form-columns-1 .input) so they match both before and after.
 *
 * SELF-CONTAINED
 * No configuration, no callbacks. It observes the form container and re-applies
 * whenever HubSpot re-renders (validation errors, dependent fields).
 *
 * MAINTENANCE
 * This corrects HubSpot's markup after render. If HubSpot changes their markup,
 * re-verify. Migrating to HubSpot's updated declarative embed would remove the
 * need for this entirely, but that renderer uses different class names and
 * would require rewriting hubspot-contact-form.css.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  // Safe if the file is included more than once (e.g. theme.liquid plus a
  // section) - only the first copy installs an observer.
  if (window.__medifyHsA11yLoaded) return;
  window.__medifyHsA11yLoaded = true;

  /*
   * Scoped to HubSpot's OWN wrapper, not a theme wrapper. Every v2 embed
   * (hbspt.forms.create) renders into a .hbspt-form container, so this covers
   * every HubSpot form on the site regardless of which section or template
   * embeds it - currently hubspot-contact-form.liquid,
   * warranty-form-section.liquid (2 forms) and the inline embed in
   * templates/page.authorized-distributors-new.json - plus any added later.
   *
   * Fieldsets outside a HubSpot form are never touched.
   */
  var FORM_ROOTS = '.hbspt-form, form.hs-form-private';
  var HIDDEN_CLASS = 'a11y-visually-hidden';
  var isPatching = false;
  var queued = false;

  /* Injected only if a real group is found and needs a legend. */
  function ensureStyles() {
    if (document.getElementById('hs-a11y-styles')) return;
    var style = document.createElement('style');
    style.id = 'hs-a11y-styles';
    style.textContent =
      '.' + HIDDEN_CLASS + '{' +
      'position:absolute !important;width:1px;height:1px;margin:-1px;padding:0;' +
      'overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);' +
      'white-space:nowrap;border:0;}';
    document.head.appendChild(style);
  }

  /* Replace a <legend> with a <div>, preserving class and children. */
  function legendToDiv(lg) {
    var div = document.createElement('div');
    div.className = lg.className;
    div.setAttribute('data-a11y-was-legend', 'true');
    while (lg.firstChild) div.appendChild(lg.firstChild);
    if (lg.parentNode) lg.parentNode.replaceChild(div, lg);
    return div;
  }

  /* Convert every <legend> inside `el`, at any depth. */
  function demoteAllLegends(el) {
    var legends = el.getElementsByTagName('legend');
    for (var i = legends.length - 1; i >= 0; i--) legendToDiv(legends[i]);
  }

  /* Convert only legends that are NOT a direct child (invalid nesting). */
  function demoteOrphanLegends(fs) {
    var legends = fs.getElementsByTagName('legend');
    for (var i = legends.length - 1; i >= 0; i--) {
      if (legends[i].parentNode !== fs) legendToDiv(legends[i]);
    }
  }

  /*
   * A genuine field group = two or more radios/checkboxes sharing a name.
   * Anything else is a HubSpot layout row and should not be a fieldset.
   */
  function isRealGroup(fs) {
    var inputs = fs.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    if (inputs.length < 2) return false;

    var seen = {};
    for (var i = 0; i < inputs.length; i++) {
      var n = inputs[i].name;
      if (!n) continue;
      seen[n] = (seen[n] || 0) + 1;
      if (seen[n] >= 2) return true;
    }
    return false;
  }

  /* Label text for a real group, derived from the labels it contains. */
  function labelForGroup(fs) {
    var labels = fs.querySelectorAll('label');
    for (var i = 0; i < labels.length; i++) {
      var lb = labels[i];
      // HubSpot renders validation messages as <label> elements too.
      if (lb.className && lb.className.indexOf('hs-error-msg') !== -1) continue;
      if (lb.closest && lb.closest('.hs-error-msgs')) continue;

      var t = (lb.textContent || '').replace(/\s+/g, ' ').trim().replace(/\*$/, '').trim();
      if (t) return t;
    }
    return 'Options';
  }

  function firstDirectLegend(fs) {
    for (var i = 0; i < fs.children.length; i++) {
      if (fs.children[i].tagName === 'LEGEND') return fs.children[i];
    }
    return null;
  }

  function unwrap(fs) {
    var div = document.createElement('div');
    for (var i = 0; i < fs.attributes.length; i++) {
      div.setAttribute(fs.attributes[i].name, fs.attributes[i].value);
    }
    div.setAttribute('data-a11y-unwrapped', 'true');
    while (fs.firstChild) div.appendChild(fs.firstChild);
    if (fs.parentNode) fs.parentNode.replaceChild(div, fs);
  }

  function process(root) {
    if (!root) return;

    // Live HTMLCollection plus node replacement - snapshot into an array first.
    var live = root.getElementsByTagName('fieldset');
    var fieldsets = [];
    for (var n = 0; n < live.length; n++) fieldsets.push(live[n]);

    for (var i = 0; i < fieldsets.length; i++) {
      var fs = fieldsets[i];

      if (isRealGroup(fs)) {
        if (fs.getAttribute('data-a11y-legend') === 'true') continue;
        demoteOrphanLegends(fs);

        var existing = firstDirectLegend(fs);
        if (existing) {
          // A legend in any position but first is ignored by assistive tech.
          if (fs.firstElementChild !== existing) fs.insertBefore(existing, fs.firstChild);
        } else {
          ensureStyles();
          var legend = document.createElement('legend');
          legend.className = HIDDEN_CLASS;
          legend.textContent = labelForGroup(fs);
          fs.insertBefore(legend, fs.firstChild);
        }
        fs.setAttribute('data-a11y-legend', 'true');
      } else {
        // Layout row, not a group. Any legend inside becomes invalid once the
        // fieldset is gone, so demote first, then unwrap.
        demoteAllLegends(fs);
        unwrap(fs);
      }
    }
  }

  function patch() {
    if (isPatching) return; // our own edits would re-trigger the observer
    isPatching = true;
    try {
      // A page may carry more than one HubSpot form (warranty-form-section
      // embeds two), so process every root, not just the first.
      var roots = document.querySelectorAll(FORM_ROOTS);
      for (var i = 0; i < roots.length; i++) process(roots[i]);
    } finally {
      isPatching = false;
    }
  }

  /* Coalesce bursts of mutations into one pass per frame. */
  function schedule() {
    if (queued) return;
    queued = true;
    var run = function () { queued = false; patch(); };
    if (window.requestAnimationFrame) window.requestAnimationFrame(run);
    else setTimeout(run, 16);
  }

  function init() {
    patch(); // in case a form rendered before this script ran

    // HubSpot injects forms asynchronously and re-renders them on validation
    // errors and dependent-field changes. The embed containers do not exist at
    // parse time, so watch the document rather than a fixed element.
    if (!window.MutationObserver) return;
    new MutationObserver(schedule)
      .observe(document.body, { childList: true, subtree: true });
  }

  // Exposed for an optional onFormReady hook; not required.
  window.medifyHubSpotFormA11y = patch;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();